import {
    Box,
    CircularProgress,
    ClickAwayListener,
    Collapse,
    IconButton,
    Link,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CircleMinus,
    Download,
    Eye,
    EyeOff,
    Menu,
    Repeat,
    Settings,
    X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import DoseItem from '~/components/DoseItem/DoseItem';
import DoseNormalization from '~/components/Header/BreadcrumbNavigation/DoseBreadcrumb/DoseDetails/DoseProperties/DoseNormalization';
import DoseProperties from '~/components/Properties/DoseProperties';
import DoseRescaler from '~/components/Header/BreadcrumbNavigation/DoseBreadcrumb/DoseDetails/DoseProperties/DoseRescaler';
import ModelDetails from '~/components/ModelDetails';
import DoseOpacityModifier from '~/components/Sliders/DoseOpacityModifier';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import useDose from '~/hooks/patient/dose/useDose';
import useResolvedDoseTargets from '~/hooks/patient/dose/useResolvedDoseTargets';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import useViewportFocus from '~/hooks/useViewportFocus';
import {
    currentDoseState,
    doseVisibilityState,
} from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import { downloadDoseToDisk } from '~/utils/io/files/downloadDicom';
import { getDoseLabel } from '../doseUtils';

type DoseHeaderProps = {
    onDoseSelect: (dose: DoseMatrix) => void;
};

export default function DoseHeader({ onDoseSelect }: DoseHeaderProps) {
    const intl = useIntl();
    const { dose, setNormalizationValue } = useDose();
    const currentDose = useRecoilValue(currentDoseState);
    const [environment] = useRecoilState(environmentState);
    const [isDoseVisible, setIsDoseVisible] =
        useRecoilState(doseVisibilityState);
    const availableDoses = useAvailableDoses(true);
    const { unloadDose } = useViewportEnvironment();
    const { focusOnPoint } = useViewportFocus();

    const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const doseLabel = dose
        ? getDoseLabel(dose.planLabel, dose.summationType)
        : undefined;

    const handleToggleProperties = () => {
        setIsPropertiesOpen(!isPropertiesOpen);
        setIsMenuOpen(false);
    };

    const handleCloseProperties = () => {
        setIsPropertiesOpen(false);
    };

    const handleToggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        setIsPropertiesOpen(false);
    };

    const handleCloseMenu = () => {
        setIsMenuOpen(false);
        setIsExpanded(false);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (
            target === document.body ||
            target.closest('[role="presentation"]') ||
            target.closest('[role="dialog"]')
        ) {
            return;
        }
        handleCloseProperties();
        handleCloseMenu();
    };

    const handleDownload = async () => {
        if (!currentDose) {
            return;
        }
        try {
            setDownloadLoading(true);
            await downloadDoseToDisk(currentDose);
        } finally {
            setDownloadLoading(false);
        }
    };

    const toggleDoseVisibility = () => {
        if (environment && isDoseVisible !== undefined) {
            setIsDoseVisible(!isDoseVisible);
            environment.updateDoseVisibility(!isDoseVisible);
        }
    };

    const handleUnload = () => {
        unloadDose();
        handleCloseMenu();
    };

    // Derive both the displayed Gy value and the click target from
    // `currentDose` so the visible label and the focus action stay in sync.
    // (`dose` from `useDose` is a view-data snapshot updated in an effect, so
    // it can briefly trail `currentDose` during a dose switch — we sourced
    // both readouts from the same DoseMatrix to avoid that mismatch.)
    const maxDoseGyDisplay = currentDose
        ? currentDose.doseMaxGy * currentDose.doseScale
        : 0;
    const maxDosePosition = currentDose?.doseMaxPosition ?? null;

    const handleFocusOnMax = () => {
        if (maxDosePosition) {
            focusOnPoint(maxDosePosition);
        }
    };

    const selectedTargets = useResolvedDoseTargets();

    return (
        <>
            <Box
                ref={headerRef}
                width="100%"
                position="relative"
                data-testid="dose-header"
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={1.5}
                >
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="dose" />
                    </Typography>
                    {currentDose && (
                        <Box display="flex" gap={0.25} mr={-0.5}>
                            <IconButton
                                size="small"
                                onClick={toggleDoseVisibility}
                                sx={{ padding: 0.5, color: 'primary.main' }}
                                data-testid="dose-visibility-toggle"
                                aria-label={intl.formatMessage({
                                    id: isDoseVisible ? 'hideDose' : 'showDose',
                                })}
                            >
                                {isDoseVisible ? (
                                    <Eye size={13} />
                                ) : (
                                    <EyeOff size={13} />
                                )}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleToggleProperties}
                                selected={isPropertiesOpen}
                                sx={{ padding: 0.5, color: 'primary.main' }}
                                data-testid="dose-properties-button"
                                aria-label={intl.formatMessage({
                                    id: 'properties',
                                })}
                            >
                                <Settings size={13} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleToggleMenu}
                                selected={isMenuOpen}
                                sx={{ padding: 0.5, color: 'primary.main' }}
                                data-testid="dose-menu-button"
                                aria-label={intl.formatMessage({
                                    id: 'doseMenu',
                                })}
                            >
                                <Menu size={14} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                {currentDose && dose ? (
                    <Box px={1.5}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="baseline"
                        >
                            <Typography
                                fontSize={12}
                                noWrap
                                data-testid="dose-label"
                            >
                                {doseLabel}
                            </Typography>
                            {maxDosePosition ? (
                                <Tooltip
                                    title={intl.formatMessage(
                                        { id: 'dose.focusOnMaxDose' },
                                        { value: maxDoseGyDisplay.toFixed(1) }
                                    )}
                                >
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={handleFocusOnMax}
                                        underline="hover"
                                        color="text.secondary"
                                        data-testid="dose-focus-max-button"
                                        aria-label={intl.formatMessage(
                                            { id: 'dose.focusOnMaxDose' },
                                            {
                                                value: maxDoseGyDisplay.toFixed(
                                                    1
                                                ),
                                            }
                                        )}
                                        sx={{
                                            display: 'inline-flex',
                                            '&:hover': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            fontSize={10}
                                            color="inherit"
                                        >
                                            Max {maxDoseGyDisplay.toFixed(1)} Gy
                                        </Typography>
                                    </Link>
                                </Tooltip>
                            ) : (
                                // Empty dose grid (no voxels) — render a plain
                                // non-interactive readout. The focus action
                                // would have nothing to target, so we don't
                                // expose a button to keyboard / screen-reader
                                // users.
                                <Typography
                                    component="span"
                                    fontSize={10}
                                    color="text.secondary"
                                    sx={{ flexShrink: 0, ml: 1 }}
                                >
                                    Max {maxDoseGyDisplay.toFixed(1)} Gy
                                </Typography>
                            )}
                        </Box>
                        {currentDose.generationModel && (
                            <ModelDetails
                                generationModel={currentDose.generationModel}
                                fullWidth
                            />
                        )}
                        {selectedTargets.length > 0 && (
                            <Box>
                                {selectedTargets.map((t) => (
                                    <Box
                                        key={t.targetName}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="baseline"
                                    >
                                        <Typography
                                            fontSize={10}
                                            noWrap
                                            color="text.secondary"
                                            sx={{ minWidth: 0 }}
                                        >
                                            {t.targetName}
                                        </Typography>
                                        <Typography
                                            fontSize={10}
                                            color="text.secondary"
                                            sx={{
                                                flexShrink: 0,
                                                ml: 1,
                                            }}
                                        >
                                            {t.doseQuantity.toFixed(1)} Gy
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box px={1.5}>
                        <Typography fontSize={12} color="text.secondary" noWrap>
                            <FormattedMessage id="noDoseLoaded" />
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Properties popper (cog icon) */}
            <Popper
                open={isPropertiesOpen && !!dose}
                anchorEl={headerRef.current}
                placement="right-start"
                sx={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-9, 1] },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            p: 1.5,
                            maxWidth: 320,
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                            borderRadius: 0,
                        })}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Typography fontSize={12} fontWeight={500}>
                                <FormattedMessage id="properties" />
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleCloseProperties}
                                sx={{ p: 0.25 }}
                                aria-label={intl.formatMessage({ id: 'close' })}
                            >
                                <X size={14} />
                            </IconButton>
                        </Box>
                        {dose && (
                            <Box display="grid" gap={2}>
                                <DoseProperties dose={dose} showTitle={false} />
                                <DoseOpacityModifier />
                                <DoseRescaler dose={dose} />
                                <DoseNormalization
                                    dose={dose}
                                    setNormalizationValue={
                                        setNormalizationValue
                                    }
                                />
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Menu popper (hamburger icon) */}
            <Popper
                open={isMenuOpen && !!currentDose}
                anchorEl={headerRef.current}
                placement="right-start"
                sx={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-9, 1] },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                            borderRadius: 0,
                        })}
                    >
                        <MenuList
                            sx={{
                                '& .MuiMenuItem-root': {
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: 0,
                                },
                            }}
                        >
                            {availableDoses.length > 1 && (
                                <>
                                    <MenuItem
                                        onClick={() =>
                                            setIsExpanded(!isExpanded)
                                        }
                                        selected={isExpanded}
                                        data-testid="change-dose-button"
                                    >
                                        <ListItemIcon>
                                            <Repeat size={14} />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="changeDose" />
                                        </ListItemText>
                                    </MenuItem>
                                    <Collapse in={isExpanded}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            maxHeight={300}
                                            overflow="auto"
                                        >
                                            {availableDoses.map((d) => {
                                                const isActive =
                                                    currentDose?.dicomUid ===
                                                    d.sopInstanceUID;
                                                const label = getDoseLabel(
                                                    d.label,
                                                    d.item?.doseSummationType
                                                );
                                                return (
                                                    <ListItemButton
                                                        key={d.sopInstanceUID}
                                                        selected={isActive}
                                                        onClick={() => {
                                                            if (d.item) {
                                                                onDoseSelect(
                                                                    d.item
                                                                );
                                                                handleCloseMenu();
                                                            }
                                                        }}
                                                        disabled={!d.isLoaded}
                                                        dense
                                                        sx={{
                                                            px: 2.5,
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            gap: 1,
                                                        }}
                                                        data-testid={`dose-change-list-item-${d.sopInstanceUID}`}
                                                    >
                                                        <DoseItem
                                                            dose={d.item!}
                                                            label={label}
                                                        />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </>
                            )}
                            <MenuItem
                                onClick={handleDownload}
                                disabled={downloadLoading}
                            >
                                <ListItemIcon>
                                    {downloadLoading ? (
                                        <CircularProgress
                                            size={14}
                                            color="primary"
                                        />
                                    ) : (
                                        <Download size={14} />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="downloadDose" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={handleUnload}
                                data-testid="unload-dose-button"
                            >
                                <ListItemIcon>
                                    <CircleMinus size={14} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="unloadDose" />
                                </ListItemText>
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
